import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { toast } from 'sonner';

import { isWorkspaceLocked } from '@/lib/consoleLock';

import { useWorkspaceStore } from '@/hooks/state';
import apiClient, { getApiErrorDetail, getApiErrorStatus } from '@/lib/apiClient';
import { STALE } from '@/lib/queryPolicy';
import {
  onboardingKey,
  onboardingLockKey,
  onboardingPath,
  type OnboardingView,
} from './onboardingQuery';
import { isOnboardingDependentQuery } from './onboardingInvalidation';

export { onboardingKey, onboardingLockKey, onboardingPath } from './onboardingQuery';

/**
 * The sequenced-onboarding contract — ONE query and two mutations for all eight
 * steps, because the transition is identical at every gate and only the payload
 * differs (backend: `core/onboarding/flow.py`).
 */

export type OnboardingStep = OnboardingView;

export type OnboardingStatus = 'researching' | 'awaiting_approval' | 'completed' | 'failed' | string;

export interface OnboardingStepStatus {
  step: OnboardingStep;
  status: OnboardingStatus | 'pending';
}

/** Row shape is per-step; every bin step carries `id` and `funded`. */
export interface OnboardingItem {
  id: string;
  [key: string]: unknown;
}

export interface OnboardingTier {
  plan: string;
  /** Included product subjects; the brand is always in scope and does not count. */
  product_limit: number | null;
  product_limit_unlimited: boolean;
  products_selected: number;
  products_discovered: number;
  matchups_funded?: number;
  matchups_discovered?: number;
  /** Tracked but possibly beyond the cap — the set "Fund first" reorders. */
  matchups_tracked?: number;
  upgrade_unlocks?: number;
  /** Canonical rival × product comparison-program contract. Null + unlimited is Enterprise. */
  program_limit: number | null;
  program_limit_unlimited: boolean;
  program_prompt_target: number;
  programs_selected: number;
  programs_discovered: number;
  programs_per_added_competitor: number;
  program_questions_planned: number;
  open_market_prompt_budget: number;
  /**
   * What the release ACTUALLY contains, measured server-side on the library
   * step. `null` everywhere else means "not measured here" — never zero. The
   * counts used to be derived from whatever rows had rendered, so a failed
   * build printed "0 head-to-head and 0 open-market questions" as though zero
   * were a deliberate library size.
   */
  program_questions_released: number | null;
  open_market_questions_released: number | null;
  library_released_total: number | null;
  /** The enforced library and engine allowances, resolved by backend entitlements. */
  library_prompt_cap: number;
  /** Guaranteed canonical pair: plain alternatives plus plain X-vs-Y. */
  plain_prompts_per_matchup: number;
  engines: string[];
  /** Selected dimension rosters that share the contextual-question room. */
  selection_counts?: {
    capabilities: number;
    requirements: number;
    buyers: number;
  };
}

export interface OnboardingState {
  /** `null` = a company EXISTS but never entered the sequenced flow (every
   *  workspace that predates the wizard). Distinct from a workspace with no
   *  company at all, which the server reports as `identity` / `not_started`.
   *  `'done'` = finished. */
  step: OnboardingStep | 'done' | null;
  status: OnboardingStatus;
  gated: boolean;
  /** THE console lock, computed server-side (`flow.is_locked`). See
   *  `lib/consoleLock.ts` for why the client does not own this rule. */
  locked?: boolean;
  /** The research pass behind the current step, for the live log. */
  research_job_id?: string | null;
  steps: OnboardingStepStatus[];
  /** Which step's findings `items` holds — equals `step` unless looking back. */
  viewing?: OnboardingStep | null;
  /** Steps already reached, and therefore readable. Earlier choices are only
   *  editable where the UI has an explicit downstream rebuild contract. */
  viewable?: OnboardingStep[];
  items: OnboardingItem[];
  /** Which steps re-run real research. A pure gate's re-run only moves the
   *  pointer back, so offering "Research again" there is a dead button. */
  steps_with_work: OnboardingStep[];
  tier: OnboardingTier | null;
  /** The findings read FAILED — distinct from an empty `items`, which means
   *  research genuinely found none here. Different copy, different affordance. */
  items_unavailable: boolean;
  job_ids: string[];
}

/**
 * While a step's research is in flight nothing else tells the page it finished,
 * so this is the one surface that genuinely polls. It stops the moment the flow
 * parks for approval or reaches a terminal state.
 */
const POLL_MS = 4000;

export function useOnboarding(
  options: { enabled?: boolean; view?: OnboardingStep | null } = {},
): UseQueryResult<OnboardingState, Error> {
  const { workspaceId } = useWorkspaceStore();
  const { enabled = true, view = null } = options;

  return useQuery<OnboardingState, Error>({
    queryKey: onboardingKey(workspaceId, view),
    queryFn: () => apiClient.get<OnboardingState>(onboardingPath(workspaceId, { view })),
    enabled: !!workspaceId && enabled,
    staleTime: STALE.live,
    retry: (count, error) => getApiErrorStatus(error) !== 404 && count < 2,
    refetchInterval: (query) => (query.state.data?.status === 'researching' ? POLL_MS : false),
  });
}

export interface ApprovalPayload {
  /** Optimistic concurrency — a mismatch is a 409 carrying the real state. */
  expected_step: OnboardingStep | 'done';
  /** BIN steps: the ids to keep. A SET; everything else is dropped. */
  tracked?: string[];
  /** REVIEW steps: per-step corrections. */
  edits?: Record<string, unknown>;
}

/** A 409 means the flow moved or is not ready — refetch instead of retrying approval. */
function handleTransitionError(error: unknown, fallback: string): void {
  if (getApiErrorStatus(error) === 409) {
    toast.error(getApiErrorDetail(error) ?? 'This step already moved on. Refreshed to where you are now.');
    return;
  }
  toast.error(getApiErrorDetail(error) ?? fallback);
}

export function useApproveStep() {
  const { workspaceId } = useWorkspaceStore();
  const queryClient = useQueryClient();

  return useMutation<OnboardingState, unknown, { step: OnboardingStep; payload: ApprovalPayload }>({
    mutationFn: ({ step, payload }) =>
      apiClient.post<OnboardingState>(`/workspaces/${workspaceId}/onboarding/${step}/approve`, payload),
    onSuccess: (next) => {
      // The endpoint returns the state AFTER the transition, so seed the cache
      // with it rather than round-tripping — the step changes under the user and
      // a refetch gap would flash the old step's rows.
      queryClient.setQueryData(onboardingKey(workspaceId), next);
      queryClient.setQueryData(onboardingLockKey(workspaceId), next);
      if (workspaceId) {
        void queryClient.invalidateQueries({
          predicate: (query) => isOnboardingDependentQuery(query.queryKey, workspaceId),
        });
      }
    },
    onError: (error) => {
      handleTransitionError(error, 'Could not save your choices.');
      void queryClient.invalidateQueries({ queryKey: onboardingKey(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: onboardingLockKey(workspaceId) });
    },
  });
}

export function useRerunStep() {
  const { workspaceId } = useWorkspaceStore();
  const queryClient = useQueryClient();

  return useMutation<OnboardingState, unknown, { step: OnboardingStep; expectedStep: OnboardingStep | 'done' }>({
    mutationFn: ({ step, expectedStep }) =>
      apiClient.post<OnboardingState>(`/workspaces/${workspaceId}/onboarding/${step}/rerun`, {
        expected_step: expectedStep,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(onboardingKey(workspaceId), next);
      queryClient.setQueryData(onboardingLockKey(workspaceId), next);
    },
    onError: (error) => {
      handleTransitionError(error, 'Could not restart that step.');
      void queryClient.invalidateQueries({ queryKey: onboardingKey(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: onboardingLockKey(workspaceId) });
    },
  });
}

/**
 * THE console lock, mounted ONCE in `ConsoleLayout` and read from there by both
 * the gate and the sidebar.
 *
 * It uses the backend's `items=false` shape so every console page asks only for
 * the indexed state row, never the current findings roster. Approval and rerun
 * mutations seed this distinct cache in the same render so the last approval
 * still opens the console without another round trip.
 *
 * A cache restore can pause the query before fetching begins. Keep the gate
 * resolving while there is no answer, including that restore window. A missing
 * workspace still has nothing to resolve.
 */
export function useConsoleLock(): { locked: boolean; resolving: boolean } {
  const { workspaceId } = useWorkspaceStore();
  const query = useQuery<OnboardingState, Error>({
    queryKey: onboardingLockKey(workspaceId),
    queryFn: () => apiClient.get<OnboardingState>(onboardingPath(workspaceId, { items: false })),
    enabled: !!workspaceId,
    staleTime: STALE.live,
    retry: (count, error) => getApiErrorStatus(error) !== 404 && count < 2,
    refetchInterval: (result) => (result.state.data?.status === 'researching' ? POLL_MS : false),
  });
  return { locked: isWorkspaceLocked(query.data), resolving: !!workspaceId && query.isPending };
}
